import React, { useEffect, useMemo, useState, Suspense } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Collapse,
  IconButton,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Accessibility as AccessibilityIcon,
  FontDownload as FontIcon,
  Palette as PaletteIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RefreshIcon,
  Animation as MotionIcon,
  TouchApp as TouchIcon,
  MenuBook as ReadingIcon,
  Visibility as VisibilityIcon,
  VolumeUp as AudioIcon,
  Keyboard as FocusIcon,
} from '@mui/icons-material';

import { useTheme } from '../../../theme/ThemeProvider';
import { useTranslation } from '../../../services/translation';

// Lazy-load each tool so disabled ones are not loaded
const LazyVisualAccessibility = React.lazy(() => import('./AccessibilityTools/VisualAccessibility').then(m => ({ default: m.VisualAccessibility })));
const LazyTextSize = React.lazy(() => import('./AccessibilityTools/TextSize').then(m => ({ default: m.TextSize })));
const LazyLayoutInteraction = React.lazy(() => import('./AccessibilityTools/LayoutInteraction').then(m => ({ default: m.LayoutInteraction })));
const LazyMotionAnimation = React.lazy(() => import('./AccessibilityTools/MotionAnimation').then(m => ({ default: m.MotionAnimation })));
const LazyFocusNavigation = React.lazy(() => import('./AccessibilityTools/FocusNavigation').then(m => ({ default: m.FocusNavigation })));
const LazyReadingComprehension = React.lazy(() => import('./AccessibilityTools/ReadingComprehension').then(m => ({ default: m.ReadingComprehension })));
const LazyColorVisibility = React.lazy(() => import('./AccessibilityTools/ColorVisibility').then(m => ({ default: m.ColorVisibility })));
const LazyAudioAlerts = React.lazy(() => import('./AccessibilityTools/AudioAlerts').then(m => ({ default: m.AudioAlerts })));

import type {
  ThemeSettings,
  AccessibilityEventData,
} from '../../../constants/interfaces/themeSettings';

import { 
  FontSizeUtils, 
  ContentDensityUtils,
  ThemeStorageUtils 
} from '../../../utils/themeSettings';

export interface AccessibilityControllerProps {
  /* Controlled theme settings value */
  value?: ThemeSettings;
  /* Callback fired when settings change */
  onChange?: (settings: ThemeSettings) => void;
  /* Whether to render in compact mode */
  compact?: boolean;
  /* The title of the controller */
  title?: string;
  /* Feature toggles to control which tools are shown/loaded */
  features?: Partial<Record<FeatureKey, boolean>>;
  /* Router integration for URL-based navigation */
  router?: {
    /* Current route parameter (e.g., 'visual-accessibility') */
    currentRoute?: string;
    /* Callback when route changes */
    onRouteChange?: (route: string) => void;
    /* Base path for routes (e.g., '/settings/accessibility') */
    basePath?: string;
  };
  onEvent?: (data: AccessibilityEventData) => void;
}

type FeatureKey =
  | 'text-size'
  | 'visual-accessibility'
  | 'layout-interaction'
  | 'motion-animation'
  | 'focus-navigation'
  | 'reading-comprehension'
  | 'color-visibility'
  | 'audio-alerts';

const DEFAULT_FEATURES: Record<FeatureKey, boolean> = {
  'text-size': true,
  'visual-accessibility': true,
  'layout-interaction': true,
  'motion-animation': true,
  'focus-navigation': true,
  'reading-comprehension': true,
  'color-visibility': true,
  'audio-alerts': true,
};

export const AccessibilityController: React.FC<AccessibilityControllerProps> = ({
  value,
  onChange,
  compact = false,
  title,
  features,
  router,
  onEvent,
}) => {
  const { t } = useTranslation();
  title = title ?? t('accessibility_controller.title');
  
  const themeContext = useTheme();
  const [isExpanded, setIsExpanded] = useState(!compact);
  const resolvedFeatures = useMemo(() => ({ ...DEFAULT_FEATURES, ...(features || {}) }), [features]);
  const firstEnabledTab = useMemo<FeatureKey>(() => {
    const order: FeatureKey[] = [
      'text-size',
      'visual-accessibility',
      'layout-interaction',
      'motion-animation',
      'focus-navigation',
      'reading-comprehension',
      'color-visibility',
      'audio-alerts',
    ];
    return order.find(key => resolvedFeatures[key]) || 'text-size';
  }, [resolvedFeatures]);

  // Initialize selected tab from router or default
  const [selectedAccessibilityTab, setSelectedAccessibilityTab] = useState<string>(() => {
    if (router?.currentRoute && resolvedFeatures[router.currentRoute as FeatureKey]) {
      return router.currentRoute;
    }
    return firstEnabledTab;
  });

  // Handle router changes
  useEffect(() => {
    if (router?.currentRoute && resolvedFeatures[router.currentRoute as FeatureKey]) {
      setSelectedAccessibilityTab(router.currentRoute);
    }
  }, [router?.currentRoute, resolvedFeatures]);

  useEffect(() => {
    // Ensure selected tab is enabled; if not, switch to first enabled
    if (!resolvedFeatures[selectedAccessibilityTab as FeatureKey]) {
      const newTab = firstEnabledTab;
      setSelectedAccessibilityTab(newTab);
      // Update router if provided
      if (router?.onRouteChange) {
        router.onRouteChange(newTab);
      }
    }
  }, [resolvedFeatures, selectedAccessibilityTab, firstEnabledTab, router]);

  // Accessibility menu items
  const accessibilityMenuItems = [
    {
      id: 'text-size',
      label: t('accessibility_menu.text_size'),
      icon: <FontIcon fontSize="small" />,
      description: t('accessibility_menu.text_size_description')
    },
    {
      id: 'visual-accessibility',
      label: t('accessibility_menu.visual_accessibility'),
      icon: <PaletteIcon fontSize="small" />,
      description: t('accessibility_menu.visual_accessibility_description')
    },
    {
      id: 'layout-interaction',
      label: t('accessibility_menu.layout_interaction'),
      icon: <TouchIcon fontSize="small" />,
      description: t('accessibility_menu.layout_interaction_description')
    },
    {
      id: 'motion-animation',
      label: t('accessibility_menu.motion_animation'),
      icon: <MotionIcon fontSize="small" />,
      description: t('accessibility_menu.motion_animation_description')
    },
    {
      id: 'focus-navigation',
      label: t('accessibility_menu.focus_navigation'),
      icon: <FocusIcon fontSize="small" />,
      description: t('accessibility_menu.focus_navigation_description')
    },
    {
      id: 'reading-comprehension',
      label: t('accessibility_menu.reading_comprehension'),
      icon: <ReadingIcon fontSize="small" />,
      description: t('accessibility_menu.reading_comprehension_description')
    },
    {
      id: 'color-visibility',
      label: t('accessibility_menu.color_visibility'),
      icon: <VisibilityIcon fontSize="small" />,
      description: t('accessibility_menu.color_visibility_description')
    },
    {
      id: 'audio-alerts',
      label: t('accessibility_menu.audio_alerts'),
      icon: <AudioIcon fontSize="small" />,
      description: t('accessibility_menu.audio_alerts_description')
    }
  ].filter(item => resolvedFeatures[item.id as FeatureKey]);

  // Render the selected accessibility component
  const renderAccessibilityComponent = () => {
    switch (selectedAccessibilityTab) {
      case 'text-size':
        return <LazyTextSize compact={compact} onEvent={onEvent} />;
        case 'visual-accessibility':
          return <LazyVisualAccessibility compact={compact} onEvent={onEvent} />;
        case 'layout-interaction':
        return <LazyLayoutInteraction compact={compact} onEvent={onEvent} />;
      case 'motion-animation':
        return <LazyMotionAnimation compact={compact} onEvent={onEvent} />;
      case 'focus-navigation':
        return <LazyFocusNavigation compact={compact} onEvent={onEvent} />;
      case 'reading-comprehension':
        return <LazyReadingComprehension compact={compact} onEvent={onEvent} />;
      case 'color-visibility':
        return <LazyColorVisibility compact={compact} onEvent={onEvent} />;
      case 'audio-alerts':
        return <LazyAudioAlerts compact={compact} onEvent={onEvent} />;
      default:
        return null;
    }
  };

  // Use controlled value or context
  const currentSettings = value || themeContext.settings;
  const handleSettingsChange = onChange || themeContext.updateSettings;

  // Handlers moved into individual AccessibilityTools components via context

  const handleReset = () => {
    ThemeStorageUtils.clearStoredSettings();
    handleSettingsChange({
      // Preserve current theme mode; reset only accessibility-related settings
      mode: currentSettings.mode,
      accessibilityMode: 'default',
      fontSize: FontSizeUtils.getSettingsFromLevel('medium'),
      contentDensity: ContentDensityUtils.getSettingsFromDensity('comfortable'),
      motion: {
        reduceMotion: false,
        animationSpeed: 'normal',
        autoPlay: true,
      },
      focusNavigation: {
        showFocusRings: true,
        enhancedKeyboardNav: false,
        skipLinks: false,
        focusTimeout: 0,
      },
      reading: {
        readingMode: false,
        lineHeight: 1.5,
        letterSpacing: 0,
        wordSpacing: 0,
        readingGuide: false,
      },
      colorVisibility: {
        highContrast: false,
        colorBlindSupport: 'none',
        saturation: 1,
        brightness: 1,
      },
      audioAlerts: {
        soundEnabled: true,
        volume: 0.5,
        visualAlerts: false,
        vibration: false,
      },
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, alignSelf: 'stretch' }}>
      <Card 
        sx={{
          borderRadius: { xs: 1, sm: 2 },
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'hidden',
          '& .MuiCardContent-root': {
            '&:last-child': { pb: 3 }
          }
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'block' }}>
          {/* Header Section */}
          <Box 
            display="flex" 
            alignItems="center" 
            justifyContent="space-between" 
            mb={3}
            pb={compact ? 0 : 2}
            borderBottom={compact ? 'none' : '1px solid'}
            borderColor="divider"
            width="100%"
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={(theme) => ({
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'success.main',
                  color: 'success.contrastText',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '& .MuiSvgIcon-root': {
                    fill: `${theme.palette.success.contrastText} !important`,
                    color: `${theme.palette.success.contrastText} !important`,
                  }
                })}
              >
                <AccessibilityIcon fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 'calc(16px * var(--unicity-font-scale, 1))', fontWeight: 600 }}>
                  {title}
                </Typography>
                <Typography sx={{ fontSize: 'calc(12px * var(--unicity-font-scale, 1))', mt: 0.25, color: 'text.secondary' }}>
                  {t('accessibility_controller.subtitle', 'Customize settings for better usability and comfort')}
                </Typography>
              </Box>
            </Box>
            {compact && (
              <IconButton 
                onClick={() => setIsExpanded(!isExpanded)}
                size="small"
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main' }
                }}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </Box>

          <Collapse in={isExpanded}>
            <Stack spacing={4}>
              {/* Accessibility Tabbed Layout */}
              <Box 
                sx={{ 
                  display: 'flex',
                  gap: 3,
                  flexDirection: { xs: 'column', md: 'row' },
                  minHeight: { md: 500 },
                }}
              >
                {/* Left Sidebar Menu */}
                <Box 
                  sx={{ 
                    minWidth: { md: 236 },
                    width: { xs: '100%', md: 236 },
                    borderColor: 'divider',
                  }}
                >
                  <List disablePadding>
                    {accessibilityMenuItems.map((item) => (
                      <ListItem key={item.id} disablePadding>
                        <ListItemButton
                          selected={selectedAccessibilityTab === item.id}
                          onClick={() => {
                            setSelectedAccessibilityTab(item.id);
                            // Update router if provided
                            if (router?.onRouteChange) {
                              router.onRouteChange(item.id);
                            }
                          }}
                          sx={(theme) => ({
                            borderRadius: 1,
                            marginBottom: 0.5,
                            // Ensure proper selected state styling and disable hover effect
                            '&.Mui-selected': {
                              backgroundColor: theme.palette.mode === 'dark' ? '#153862' : 'primary.main',
                              color: '#ffffff', // Always white text for maximum contrast
                              borderRadius: '12px',
                              '& .MuiListItemIcon-root': {
                                color: '#ffffff', // Always white icons
                              },
                              '& .MuiListItemIcon-root .MuiSvgIcon-root': {
                                fill: '#ffffff !important', // Always white icons with !important
                                color: '#ffffff !important', // Force white with !important
                              },
                              '& .MuiListItemIcon-root svg': {
                                fill: '#ffffff !important', // Always white SVG icons
                                color: '#ffffff !important', // Force white with !important
                              },
                              '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                                color: '#ffffff', // Always white text
                              },
                              '&:hover': {
                                backgroundColor: theme.palette.mode === 'dark' ? '#0051cc' : 'primary.dark',
                                cursor: 'default',
                              }
                            },
                            // Normal hover for non-selected items
                            '&:hover:not(.Mui-selected)': {
                              backgroundColor: 'action.hover',
                              borderRadius: '12px',
                            },
                            // Ensure normal (non-selected) items are visible in dark mode
                            '& .MuiListItemIcon-root .MuiSvgIcon-root': {
                              color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                            },
                            '& .MuiListItemText-primary, & .MuiListItemText-secondary': {
                              color: theme.palette.mode === 'dark' ? '#ffffff' : 'inherit',
                            }
                          })}
                        >
                          <ListItemIcon 
                            sx={{
                              minWidth: 32,
                              pr: 3,
                              pl: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '& .MuiSvgIcon-root': {
                                fontSize: 'calc(1.25rem * var(--unicity-font-scale, 1))',
                              }
                            }}
                          >
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography 
                                fontWeight={selectedAccessibilityTab === item.id ? 600 : 400}
                                sx={{ 
                                  fontSize: 'calc(12px * var(--unicity-font-scale, 1))'
                                }}
                              >
                                {item.label}
                              </Typography>
                            }
                            secondary={
                              <Typography 
                                sx={{ 
                                  fontSize: 'calc(10px * var(--unicity-font-scale, 1))',
                                  display: { xs: 'none', md: 'block' },
                                  opacity: 0.8
                                }}
                              >
                                {item.description}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>

                {/* Right Content Area */}
                <Box 
                  sx={{ 
                    flex: 1,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2.5,
                    overflow: 'auto',
                  }}
                >
                  <Suspense fallback={null}>
                    {renderAccessibilityComponent()}
                  </Suspense>
                </Box>
              </Box>

              {/* Reset Section */}
              <Box 
                sx={{ 
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'center',
                }}
              >
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleReset}
                  startIcon={<RefreshIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1,
                    textTransform: 'none',
                    fontSize: 'calc(12px * var(--unicity-font-scale, 1))',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: 'warning.light',
                      borderColor: 'warning.main',
                    }
                  }}
                >
                  {t('accessibility_controller.reset')}
                </Button>
                <Typography sx={{ fontSize: 'calc(12px * var(--unicity-font-scale, 1))', color: 'text.secondary', mt: 1 }}>
                  {t('accessibility_controller.reset_description')}
                </Typography>
              </Box>
            </Stack>
          </Collapse>
        </CardContent>
      </Card>
    </Box>
  );
};
